import { Outlet } from 'react-router';
import { useSelectedClient } from '@/core/selectedClient';
import { filesNothingAtCompaniesHouse, useClientType } from '@/core/useClientType';
import { AsideLayout, type AsideItem } from './AsideMenu';
import './TaxreturnLayout.scss';

const CH_INTEGRATION_ROUTE = '/home/taxreturns/integrations-companyofficers';

// APT359 (client, 30 Oct 2026): the order is Dashboard, Tasks, Reports, Settings, Companies House, Activity Log; the last two were "CH API's Integration" and "Logs".
const items: AsideItem[] = [
  { label: 'Dashboard', icon: 'person_outline', to: '/home/taxreturns/dashboard' },
  {
    label: 'Tasks',
    icon: 'task',
    to: '/home/taxreturns/tasks-trialbalance',
    alsoActiveFor: [
      '/home/taxreturns/tasks-submit',
      '/home/taxreturns/tasks-cicsubmission',
      '/home/taxreturns/tasks-importexceptions',
      '/home/taxreturns/tasks-nominalmapping',
      '/home/taxreturns/tasks-adjustedtrialbalance',
      '/home/taxreturns/tasks-journals',
      '/home/taxreturns/working-papers',
      '/home/taxreturns/statutory-database',
      '/home/taxreturns/accounts-builder',
      '/home/taxreturns/validation-centre',
      '/home/taxreturns/tasks-reviewpoints',
      '/home/taxreturns/tasks-approval',
      '/home/taxreturns/ixbrl-facts',
      '/home/taxreturns/filing-status',
      '/home/taxreturns/tax-handoff',
      '/home/taxreturns/tasks-profitallocation',
      '/home/taxreturns/tasks-partneraccounts',
      '/home/taxreturns/tasks-groupstructure',
      '/home/taxreturns/tasks-grouptrialbalance',
      '/home/taxreturns/tasks-groupjournals',
    ],
  },
  {
    label: 'Reports',
    icon: 'poll',
    to: '/home/taxreturns/reports-accounts',
    alsoActiveFor: [
      '/home/taxreturns/reports-managementaccounts',
      '/home/taxreturns/reports-cashflows',
      '/home/taxreturns/reports-trialbalance',
      '/home/taxreturns/reports-ladger',
      '/home/taxreturns/reports-managementperiods',
      '/home/taxreturns/reports-periodictrialbalance',
      '/home/taxreturns/reports-budgets',
      '/home/taxreturns/reports-dimensions',
      '/home/taxreturns/reports-managementpacks',
      '/home/taxreturns/reports-cicreports',
    ],
  },
  {
    label: 'Settings',
    icon: 'settings',
    to: '/home/taxreturns/tasks-accountingperiods',
    alsoActiveFor: [
      '/home/taxreturns/settings-datasecurity',
      '/home/taxreturns/settings-accoununts',
      '/home/taxreturns/settings-reportformat',
      '/home/taxreturns/settings-reportsettings',
      '/home/taxreturns/company-contact',
      '/home/taxreturns/director-report',
      '/home/taxreturns/share-capital',
      '/home/taxreturns/accountant-report',
      '/home/taxreturns/accounting-policy',
      '/home/taxreturns/additional-notes',
      '/home/taxreturns/customise-headings',
      '/home/taxreturns/revised-accounts',
      '/home/taxreturns/cic-34-report',
      '/home/taxreturns/cic34-report',
      '/home/taxreturns/llp-info',
      '/home/taxreturns/revisions-lineage',
      '/home/taxreturns/regulatory-pack-upgrade',
      '/home/taxreturns/fixed-asset-sync',
    ],
  },
  {
    label: 'Companies House',
    icon: 'integration_instructions',
    to: CH_INTEGRATION_ROUTE,
    alsoActiveFor: ['/home/taxreturns/integrations-persons', '/home/taxreturns/integrations-history'],
  },
  {
    label: 'Activity Log',
    icon: 'history',
    to: '/home/taxreturns/emaillogs',
    alsoActiveFor: ['/home/taxreturns/userlogs'],
  },
];

/**
 * Names the client whose accounts are open. The client lives in a cookie and can change without this layout being
 * recreated (Angular's getter re-read it on every change detection).
 *
 * APT244: reading the cookie during render was not enough: this layout is not rendered again when the client is
 * switched on a screen inside it (the dashboard's client dropdown) or on navigating between its screens, so the bar
 * kept the first client. It now re-renders whenever the selected client changes.
 */
export function TaxreturnLayout() {
  const client = useSelectedClient();
  // Sole traders and partnerships do not use the Companies House API integration.
  const noCompaniesHouse = filesNothingAtCompaniesHouse(useClientType(client?.id));
  const visibleItems = noCompaniesHouse ? items.filter((item) => item.to !== CH_INTEGRATION_ROUTE) : items;

  return (
    // Every tab here (Tasks/Settings/Reports/CH API's Integration/Logs) renders via SideMenu, which has its own
    // collapse arrow for its sub-menu list - same reason ManageLayout passes this, see AsideLayout's doc comment.
    <AsideLayout items={visibleItems} collapsible={false}>
      {/* The client switcher now renders in the top navy header bar (Header.tsx), not here - see TaxreturnLayout.scss
          for the .form height rule this used to require and no longer does. */}
      {/* Keyed by the client, so every screen opens afresh on the client chosen in the bar. */}
      <Outlet key={client?.id ?? 'none'} />
    </AsideLayout>
  );
}
